/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { ManageUrgencyType, NotificationListEventsInner } from "client";
import { FC, Fragment, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  Stack,
  IconButton,
  useTheme,
  Box,
  Backdrop,
} from "@mui/material";
import { Const } from "const";
import { Spinner } from "components/Common/Spinner";
import { BorderedButton } from "components/Common/BorderedButton";
import { NotificationDetailDialog } from "./NotificationDetailDialog";
import InformationIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import KeyboardControlKeyIcon from "@mui/icons-material/KeyboardControlKey";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SystemErrorDialog } from "components/Common/SystemErrorDialog";
import { useNotificationList } from "hooks/GetNotificationListHook";
import { TimeoutError } from "exception/TimeoutError";
import { ConfirmDialog } from "components/Common/ConfirmDialog";
import moment from "moment";

interface Props {
  onClose: () => void;
}

const SpinnerDelayInnerList: FC = () => {
  const [display, setDisplay] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => {
      setDisplay(true);
    }, Const.spinnerDelayInterval);
    return () => {
      clearTimeout(id);
    };
  }, []);
  return (
    <Backdrop open={display} invisible={true} sx={{ position: "absolute" }}>
      <Spinner />
    </Backdrop>
  );
};

export const NotificationListDialog: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation(["monitor", "notification"]);
  const [notifications, setNotifications] =
    useState<Array<NotificationListEventsInner>>();
  const nextKeyRef = useRef<string>();
  const [next, setNext] = useState<string>();
  const [selected, setSelected] = useState<NotificationListEventsInner>();
  const [openDetail, setOpenDetail] = useState(false);
  const [isTop, setIsTop] = useState(true);
  const [isBottom, setIsBottom] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const { loading, error, value } = useNotificationList(next);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (value) {
      nextKeyRef.current = value.nextKey;
      setNotifications((prev) => {
        if (value.events) {
          if (prev) {
            return [...prev, ...value.events];
          } else {
            return value.events;
          }
        } else {
          // not modify.
          return prev;
        }
      });
    }
    if (error) {
      setShowError(true);
    }
  }, [value, error]);

  const handleScroll = () => {
    if (listRef.current) {
      setIsTop(listRef.current.scrollTop === 0);
      if (
        Math.floor(listRef.current.scrollTop + listRef.current.clientHeight) >=
        listRef.current.scrollHeight - 1
      ) {
        setIsBottom(true);
        if (nextKeyRef.current) {
          // next item.
          setNext(nextKeyRef.current);
        }
      } else {
        setIsBottom(false);
      }
    }
  };

  const handleUp = () => {
    if (listRef.current) {
      const itemHeight = listRef.current.scrollHeight / notifications!.length;
      const target = listRef.current.scrollTop - itemHeight * 3;
      listRef.current.scrollTo({
        top: Math.max(0, target),
        behavior: "smooth",
      });
    }
  };

  const handleDown = () => {
    if (listRef.current) {
      if (listRef.current.clientHeight === listRef.current.scrollHeight) {
        setIsBottom(true);
        // no scroll
        if (nextKeyRef.current) {
          setNext(nextKeyRef.current);
        }
      } else {
        setIsBottom(false);
        const itemHeight = listRef.current.scrollHeight / notifications!.length;
        const target = listRef.current.scrollTop + itemHeight * 3;
        listRef.current.scrollTo({
          top: target,
          behavior: "smooth",
        });
      }
    }
  };

  const getDescription = (notification: NotificationListEventsInner) => {
    const dateStr = moment(notification.notifiedAt).format(
      Const.dateTimeFormat
    );
    const eventId = `[${notification.eventId}]`;
    const title = t(`AWV-UI-SUBJECT-${notification.eventId!}`, {
      ns: "notification",
    });
    const message = t(`AWV-UI-BODY-${notification.eventId!}`, {
      ns: "notification",
      frameTypeAndNumber: notification.frameTypeAndNumber,
      taskId: notification.taskIdDisp,
    });
    return `${dateStr} ${eventId} ${title} ${message}`;
  };

  useEffect(() => {
    if (notifications && listRef.current) {
      if (
        listRef.current.clientHeight === listRef.current.scrollHeight ||
        Math.floor(listRef.current.scrollTop + listRef.current.clientHeight) >=
          listRef.current.scrollHeight - 1
      ) {
        setIsBottom(true);
      } else {
        setIsBottom(false);
      }
    }
  }, [notifications]);

  return (
    <>
      <Dialog
        open={true}
        sx={{
          "& .MuiDialog-paper": {
            width: 800,
            height: "90vh",
            bgcolor: theme.palette.grey[400],
            justifyContent: "center",
            textAlign: "center",
          },
        }}
      >
        <DialogTitle>{t("AWVUI-MSG124")}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              width: "100%",
              bgcolor: "background.paper",
              position: "relative",
              overflow: "hidden",
              height: `calc(100% - 32px)`,
            }}
          >
            <List
              ref={listRef}
              component="nav"
              onScroll={handleScroll}
              sx={{
                width: "100%",
                bgcolor: "background.paper",
                position: "relative",
                overflow: "auto",
                height: "100%",
              }}
            >
              {notifications?.map((notification, i) => (
                <Fragment key={`fragment-${i}`}>
                  <ListItemButton
                    key={`item-${i}`}
                    selected={notification === selected}
                    onClick={() => {
                      setSelected(notification);
                      setOpenDetail(true);
                    }}
                  >
                    <ListItemIcon key={`itemicon-${i}`}>
                      {notification.urgency === ManageUrgencyType.Urgent ? (
                        <WarningIcon color="error" />
                      ) : (
                        <InformationIcon />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      key={`itemtext-${i}`}
                      primary={getDescription(notification)}
                      primaryTypographyProps={{
                        style: {
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  </ListItemButton>
                  <Divider key={`divider-${i}`} />
                </Fragment>
              ))}
            </List>
            {loading && <SpinnerDelayInnerList />}
          </Box>
          <Stack
            direction="row"
            justifyContent="center"
            spacing={2}
            sx={{ height: 32 }}
          >
            <IconButton disabled={!notifications || isTop} onClick={handleUp}>
              <KeyboardControlKeyIcon fontSize="large" />
            </IconButton>
            <IconButton
              disabled={!notifications || (isBottom && !nextKeyRef.current)}
              onClick={handleDown}
            >
              <KeyboardArrowDownIcon fontSize="large" />
            </IconButton>
          </Stack>
        </DialogContent>
        <DialogActions>
          <BorderedButton onClick={onClose}>{t("AWVUI-MSG22")}</BorderedButton>
        </DialogActions>
      </Dialog>
      {openDetail && (
        <NotificationDetailDialog
          open={openDetail}
          onClose={() => {
            setOpenDetail(false);
          }}
          notification={selected!}
        />
      )}
      {error && error instanceof TimeoutError ? (
        <ConfirmDialog
          open={showError}
          message={t("AWVUI-MSG125")}
          positiveButtonText={t("AWVUI-MSG22")}
          onPositiveButtonClick={() => {
            setShowError(false);
          }}
        />
      ) : (
        <SystemErrorDialog
          open={showError}
          error={error}
          onClose={() => {
            setShowError(false);
          }}
        />
      )}
    </>
  );
};
